'use client';

import { getProblemTypeTitle } from '@/constants/problem-type-titles';
import { ProductAnalysisPointEntity } from '@/entities/product-analysis.entity';
import { FC, useMemo } from 'react';

type ProductAnalysisRadarChartProps = {
    points: ProductAnalysisPointEntity[];
};

const CHART_WIDTH = 1080;
const CHART_HEIGHT = 560;
const CHART_CENTER_X = CHART_WIDTH / 2;
const CHART_CENTER_Y = CHART_HEIGHT / 2;
const CHART_RADIUS = 180;
const GRID_LEVELS = 5;

const isOnLeftSide = (angle: number) => {
    return Math.cos(angle) < -0.2;
};

const isOnRightSide = (angle: number) => {
    return Math.cos(angle) > 0.2;
};

const toCoordinates = (angle: number, radius: number) => {
    return {
        x: CHART_CENTER_X + Math.cos(angle) * radius,
        y: CHART_CENTER_Y + Math.sin(angle) * radius,
    };
};

const toRounded = (value: number): number => {
    return Number(value.toFixed(2));
};

const normalizeMaxValue = (value: number): number => {
    if (!Number.isFinite(value) || value <= 0) {
        return 5;
    }

    return Math.max(5, Math.ceil(value * 2) / 2);
};

const formatIntensity = (value: number): string => {
    return value.toLocaleString('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
};

const getScaleLabelProps = (
    angle: number,
    radius: number,
): {
    x: number;
    y: number;
    textAnchor: 'start' | 'middle' | 'end';
    dominantBaseline: 'middle' | 'hanging' | 'auto';
} => {
    const position = toCoordinates(angle, radius + 9);

    if (isOnRightSide(angle)) {
        return {
            x: toRounded(position.x + 2),
            y: toRounded(position.y),
            textAnchor: 'start',
            dominantBaseline: 'middle',
        };
    }

    if (isOnLeftSide(angle)) {
        return {
            x: toRounded(position.x - 2),
            y: toRounded(position.y),
            textAnchor: 'end',
            dominantBaseline: 'middle',
        };
    }

    if (Math.sin(angle) > 0) {
        return {
            x: toRounded(position.x),
            y: toRounded(position.y + 1),
            textAnchor: 'middle',
            dominantBaseline: 'hanging',
        };
    }

    return {
        x: toRounded(position.x),
        y: toRounded(position.y - 1),
        textAnchor: 'middle',
        dominantBaseline: 'auto',
    };
};

const ProductAnalysisRadarChart: FC<ProductAnalysisRadarChartProps> = ({ points }) => {
    const chartData = useMemo(() => {
        const axisCount = points.length;
        const highestIntensity = Math.max(
            0,
            ...points.map((point) =>
                Math.max(
                    point.positiveAverageIntensity,
                    point.negativeAverageIntensity,
                    point.neutralAverageIntensity || 0,
                ),
            ),
        );
        const maxIntensity = normalizeMaxValue(highestIntensity);

        const axis = points.map((point, index) => {
            const angle = -Math.PI / 2 + (Math.PI * 2 * index) / axisCount;
            const axisEnd = toCoordinates(angle, CHART_RADIUS);
            const problemTypeTitle = getProblemTypeTitle(point.problemType) || point.problemType;
            const positiveRadius = (Math.max(0, point.positiveAverageIntensity) / maxIntensity) * CHART_RADIUS;
            const negativeRadius = (Math.max(0, point.negativeAverageIntensity) / maxIntensity) * CHART_RADIUS;
            const neutralRadius = (Math.max(0, point.neutralAverageIntensity || 0) / maxIntensity) * CHART_RADIUS;
            const positiveMarker = toCoordinates(angle, positiveRadius);
            const negativeMarker = toCoordinates(angle, negativeRadius);
            const neutralMarker = toCoordinates(angle, neutralRadius);

            return {
                point,
                problemTypeTitle,
                angle,
                axisEnd,
                positiveMarker,
                negativeMarker,
                neutralMarker,
            };
        });

        const toPolygon = (mode: 'positive' | 'negative' | 'neutral') => {
            return axis
                .map(({ positiveMarker, negativeMarker, neutralMarker }) => {
                    const coords =
                        mode === 'positive' ? positiveMarker : mode === 'negative' ? negativeMarker : neutralMarker;
                    return `${toRounded(coords.x)},${toRounded(coords.y)}`;
                })
                .join(' ');
        };

        const levels = new Array(GRID_LEVELS).fill(false).map((_, levelIndex) => {
            const level = levelIndex + 1;
            const radius = (CHART_RADIUS / GRID_LEVELS) * level;
            const polygon = axis
                .map(({ angle }) => {
                    const coords = toCoordinates(angle, radius);
                    return `${toRounded(coords.x)},${toRounded(coords.y)}`;
                })
                .join(' ');

            return {
                polygon,
                radius,
                value: formatIntensity((maxIntensity / GRID_LEVELS) * level),
            };
        });

        return {
            axis,
            levels,
            positivePolygon: toPolygon('positive'),
            negativePolygon: toPolygon('negative'),
            neutralPolygon: toPolygon('neutral'),
        };
    }, [points]);

    if (!points.length) {
        return (
            <div className="flex min-h-[260px] items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm text-slate-600">
                Нет данных по типам проблем для построения графика.
            </div>
        );
    }

    return (
        <div className="w-full overflow-x-auto">
            <div className="min-w-[1140px] rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-slate-800">Веб-граф проблем по типам</h3>
                <p className="mt-1 text-xs text-slate-500">
                    Ось: тип проблемы. Значение: средняя интенсивность по каждому типу.
                </p>
                <p className="text-xs text-slate-500">
                    Зеленая линия: позитивный сентимент. Серая линия: нейтральный сентимент. Красная линия: негативный
                    сентимент.
                </p>
                <p className="text-xs text-slate-500">Малые круги на осях показывают точные значения каждого ряда.</p>
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600">
                    <div className="flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />
                        <span>Позитивный сентимент</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-500" />
                        <span>Нейтральный сентимент</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-full bg-rose-500" />
                        <span>Негативный сентимент</span>
                    </div>
                </div>
                <div className="mt-4 flex justify-center">
                    <svg
                        width={CHART_WIDTH}
                        height={CHART_HEIGHT}
                        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                        role="img"
                        aria-label="Веб-граф средних интенсивностей проблем по позитивному, нейтральному и негативному сентименту"
                    >
                        {chartData.levels.map((level, index) => (
                            <g key={`grid-level-${index}`}>
                                <polygon points={level.polygon} fill="none" stroke="#e2e8f0" strokeWidth="1" />
                            </g>
                        ))}
                        <text
                            x={CHART_CENTER_X + 5}
                            y={CHART_CENTER_Y - 2}
                            fontSize="9"
                            fill="#64748b"
                            textAnchor="start"
                        >
                            0
                        </text>

                        {chartData.axis.map(({ axisEnd, angle, point, problemTypeTitle }) => (
                            <g key={`axis-${point.problemType}`}>
                                <line
                                    x1={CHART_CENTER_X}
                                    y1={CHART_CENTER_Y}
                                    x2={axisEnd.x}
                                    y2={axisEnd.y}
                                    stroke="#cbd5e1"
                                    strokeWidth="1"
                                />
                                {chartData.levels.map((level, levelIndex) => {
                                    const axisScalePosition = toCoordinates(angle, level.radius);
                                    const normalAngle = angle + Math.PI / 2;
                                    const tickHalfLength = 2.5;
                                    const tickStartX = toRounded(
                                        axisScalePosition.x - Math.cos(normalAngle) * tickHalfLength,
                                    );
                                    const tickStartY = toRounded(
                                        axisScalePosition.y - Math.sin(normalAngle) * tickHalfLength,
                                    );
                                    const tickEndX = toRounded(
                                        axisScalePosition.x + Math.cos(normalAngle) * tickHalfLength,
                                    );
                                    const tickEndY = toRounded(
                                        axisScalePosition.y + Math.sin(normalAngle) * tickHalfLength,
                                    );
                                    const labelProps = getScaleLabelProps(angle, level.radius);

                                    return (
                                        <g key={`axis-${point.problemType}-level-${levelIndex}`}>
                                            <line
                                                x1={tickStartX}
                                                y1={tickStartY}
                                                x2={tickEndX}
                                                y2={tickEndY}
                                                stroke="#94a3b8"
                                                strokeWidth="1"
                                            />
                                            <text
                                                x={labelProps.x}
                                                y={labelProps.y}
                                                fontSize="9"
                                                fill="#64748b"
                                                textAnchor={labelProps.textAnchor}
                                                dominantBaseline={labelProps.dominantBaseline}
                                            >
                                                {level.value}
                                            </text>
                                        </g>
                                    );
                                })}
                                <text
                                    x={toRounded(
                                        CHART_CENTER_X +
                                            Math.cos(angle) *
                                                (CHART_RADIUS + 18 + Math.max(problemTypeTitle.length - 12, 0) * 0.6),
                                    )}
                                    y={toRounded(CHART_CENTER_Y + Math.sin(angle) * (CHART_RADIUS + 18))}
                                    fontSize="12"
                                    fill="#334155"
                                    textAnchor={isOnLeftSide(angle) ? 'end' : isOnRightSide(angle) ? 'start' : 'middle'}
                                    dominantBaseline="middle"
                                >
                                    {problemTypeTitle}
                                    <title>{problemTypeTitle}</title>
                                </text>
                            </g>
                        ))}

                        <polygon
                            points={chartData.positivePolygon}
                            fill="rgba(16, 185, 129, 0.18)"
                            stroke="#10b981"
                            strokeWidth="2"
                        />
                        <polygon
                            points={chartData.neutralPolygon}
                            fill="rgba(100, 116, 139, 0.16)"
                            stroke="#64748b"
                            strokeWidth="2"
                        />
                        <polygon
                            points={chartData.negativePolygon}
                            fill="rgba(244, 63, 94, 0.18)"
                            stroke="#f43f5e"
                            strokeWidth="2"
                        />

                        {chartData.axis.map(({ point, positiveMarker, neutralMarker, negativeMarker }) => (
                            <g key={`marker-${point.problemType}`}>
                                <circle
                                    cx={toRounded(positiveMarker.x)}
                                    cy={toRounded(positiveMarker.y)}
                                    r="3"
                                    fill="#10b981"
                                    stroke="#ffffff"
                                    strokeWidth="1"
                                />
                                <circle
                                    cx={toRounded(neutralMarker.x)}
                                    cy={toRounded(neutralMarker.y)}
                                    r="3"
                                    fill="#64748b"
                                    stroke="#ffffff"
                                    strokeWidth="1"
                                />
                                <circle
                                    cx={toRounded(negativeMarker.x)}
                                    cy={toRounded(negativeMarker.y)}
                                    r="3"
                                    fill="#f43f5e"
                                    stroke="#ffffff"
                                    strokeWidth="1"
                                />
                            </g>
                        ))}
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default ProductAnalysisRadarChart;
